import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/product/ProductCard";
import { Search, Info } from "lucide-react";

export default function PartSearch() {
  const [params, setParams] = useSearchParams();
  const [pn, setPn] = useState(params.get("pn") || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (v) => {
    if (!v.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/search/part", { params: { pn: v } });
      setResults(data);
      setParams({ pn: v });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("pn")) run(params.get("pn"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="part-search-page" className="bg-white">
      <div className="bg-[#0B0F19] text-white pt-12 pb-16 lg:pt-16 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 ici-grid-bg-dark opacity-40" />
        <div className="max-w-3xl mx-auto px-4 lg:px-8 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">Part number search</div>
          <h1 className="mt-2 font-display text-3xl lg:text-5xl font-extrabold tracking-tight">Search by OEM or manufacturer part number</h1>
          <p className="mt-3 text-slate-400">Enter the number printed on your current injector or component. The search recognizes common formatting differences, including spaces and dashes.</p>
          <form onSubmit={(e) => { e.preventDefault(); run(pn); }} className="mt-6 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="part-search-input" autoFocus value={pn} onChange={(e) => setPn(e.target.value)} placeholder="e.g. 23250-0D040 · 12594512 · 0280155710" className="pl-10 h-12 bg-white/5 border-white/10 text-white font-mono placeholder:text-slate-500" />
            </div>
            <Button data-testid="part-search-submit" className="bg-[#0055FF] hover:bg-[#0044CC] h-12 px-6">Search</Button>
          </form>
          <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5"><Info className="w-3 h-3" /> Example: try 23250-0D040 or 12668390</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        {loading && <div className="text-sm text-slate-500">Searching…</div>}
        {results && (
          <div className="space-y-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Exact matches <span className="text-slate-400 font-mono text-sm">({results.exact.length})</span></h2>
              {results.exact.length === 0 ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">No exact match. See partial matches below.</div>
              ) : (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {results.exact.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
                </div>
              )}
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Partial matches <span className="text-slate-400 font-mono text-sm">({results.partial.length})</span></h2>
              {results.partial.length === 0 ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
                  No partial matches. <Link to="/request-a-part" className="text-[#0055FF] hover:underline">Request this part →</Link>
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {results.partial.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
